import React, { useState, useEffect, useMemo, useRef } from "react";
import { useTranslation } from "react-i18next";
import { X, PackageCheck } from "lucide-react";
import * as DialogPrimitive from "@radix-ui/react-dialog";
import { cn } from "../../../lib/utils";
import {
  Dialog,
  DialogOverlay,
  DialogPortal,
  DialogTitle,
} from "../../../components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../../components/ui/select";
import { inventoryManagementService } from "../../../services/inventoryManagementService";

function todayIsoDate() {
  return new Date().toISOString().slice(0, 10);
}

const fieldLabelClass =
  "text-[10px] font-extrabold uppercase tracking-wider text-gray-400 block mb-1";
const fieldInputClass =
  "w-full h-9 px-2.5 rounded-lg border border-(--color-text-border) bg-white text-xs font-semibold focus:border-accent focus:outline-hidden disabled:bg-gray-50 disabled:text-gray-400";
const selectTriggerClass = cn(
  fieldInputClass,
  "flex items-center justify-between [&>span]:truncate"
);

/**
 * @param {{
 *   open: boolean,
 *   onOpenChange: (open: boolean) => void,
 *   customerId?: string|number|null,
 *   products?: Array<{
 *     id: string,
 *     name?: string,
 *     batchNumber?: string,
 *     allocatedQty?: number|string,
 *     deliveryDate?: string,
 *     invoiceNumber?: string,
 *     deliveryNotes?: string,
 *   }>,
 *   submitting?: boolean,
 *   onConfirm: (selected: Array<{
 *     lineId: string,
 *     deliveryDate: string,
 *     invoiceNumber: string,
 *     deliveryNotes: string,
 *     challanToAddress: string,
 *     challanToMobile: string,
 *   }>) => void,
 * }} props
 */
export function MarkAsDeliveredModal({
  open,
  onOpenChange,
  customerId = null,
  products = [],
  submitting = false,
  onConfirm,
}) {
  const { t } = useTranslation();
  const [selectedIds, setSelectedIds] = useState([]);
  const [datesById, setDatesById] = useState({});
  const [invoiceById, setInvoiceById] = useState({});
  const [notesById, setNotesById] = useState({});
  const [addressIdByLine, setAddressIdByLine] = useState({});
  const [mobileIdByLine, setMobileIdByLine] = useState({});
  const [locations, setLocations] = useState([]);
  const [mobiles, setMobiles] = useState([]);
  const [loadingLocations, setLoadingLocations] = useState(false);
  const [loadingMobiles, setLoadingMobiles] = useState(false);
  const [error, setError] = useState("");

  const productIds = useMemo(
    () => products.map((p) => String(p.id)),
    [products]
  );
  const productIdsKey = productIds.join("|");
  const productsRef = useRef(products);
  productsRef.current = products;

  useEffect(() => {
    if (!open) return;
    const nextDates = {};
    const nextInvoices = {};
    const nextNotes = {};
    productsRef.current.forEach((p) => {
      const id = String(p.id);
      nextDates[id] = p.deliveryDate || todayIsoDate();
      nextInvoices[id] =
        p.invoiceNumber && p.invoiceNumber !== "—"
          ? String(p.invoiceNumber)
          : "";
      nextNotes[id] = p.deliveryNotes ? String(p.deliveryNotes) : "";
    });
    setDatesById(nextDates);
    setInvoiceById(nextInvoices);
    setNotesById(nextNotes);
    setSelectedIds(productsRef.current.map((p) => String(p.id)));
    setError("");
  }, [open, productIdsKey]);

  useEffect(() => {
    if (!open) return undefined;

    setLocations([]);
    setMobiles([]);
    setAddressIdByLine({});
    setMobileIdByLine({});

    if (customerId == null || String(customerId).trim() === "") {
      setLoadingLocations(false);
      setLoadingMobiles(false);
      return undefined;
    }

    let active = true;
    setLoadingLocations(true);
    setLoadingMobiles(true);

    inventoryManagementService
      .getTransferCustomerLocations({ customerId })
      .then((rows) => {
        if (!active) return;
        const list = rows || [];
        setLocations(list);
        const firstId = list[0]?.id != null ? String(list[0].id) : "";
        if (firstId) {
          const next = {};
          productsRef.current.forEach((p) => {
            next[String(p.id)] = firstId;
          });
          setAddressIdByLine(next);
        }
      })
      .catch((err) => {
        if (!active) return;
        console.error("Failed to load customer locations:", err);
        setLocations([]);
      })
      .finally(() => {
        if (active) setLoadingLocations(false);
      });

    inventoryManagementService
      .getTransferCustomerMobiles({ customerId })
      .then((rows) => {
        if (!active) return;
        const list = rows || [];
        setMobiles(list);
        const firstId = list[0]?.id != null ? String(list[0].id) : "";
        if (firstId) {
          const next = {};
          productsRef.current.forEach((p) => {
            next[String(p.id)] = firstId;
          });
          setMobileIdByLine(next);
        }
      })
      .catch((err) => {
        if (!active) return;
        console.error("Failed to load customer contacts:", err);
        setMobiles([]);
      })
      .finally(() => {
        if (active) setLoadingMobiles(false);
      });

    return () => {
      active = false;
    };
  }, [open, customerId, productIdsKey]);

  const locationById = useMemo(() => {
    const map = new Map();
    locations.forEach((row) => map.set(String(row.id), row));
    return map;
  }, [locations]);

  const mobileById = useMemo(() => {
    const map = new Map();
    mobiles.forEach((row) => map.set(String(row.id), row));
    return map;
  }, [mobiles]);

  const allSelected =
    productIds.length > 0 && productIds.every((id) => selectedIds.includes(id));
  const someSelected = selectedIds.length > 0 && !allSelected;

  const toggleAll = () => {
    setSelectedIds(allSelected ? [] : [...productIds]);
    setError("");
  };

  const toggleOne = (id) => {
    setSelectedIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
    setError("");
  };

  const setDate = (id, value) => {
    setDatesById((prev) => ({ ...prev, [id]: value }));
    setError("");
  };

  const setInvoice = (id, value) => {
    setInvoiceById((prev) => ({ ...prev, [id]: value }));
    setError("");
  };

  const setNotes = (id, value) => {
    setNotesById((prev) => ({ ...prev, [id]: value }));
    setError("");
  };

  const setAddressId = (id, value) => {
    setAddressIdByLine((prev) => ({ ...prev, [id]: value }));
    setError("");
  };

  const setMobileId = (id, value) => {
    setMobileIdByLine((prev) => ({ ...prev, [id]: value }));
    setError("");
  };

  const resolveAddressLabel = (lineId) => {
    const option = locationById.get(String(addressIdByLine[lineId] || ""));
    return String(option?.address || option?.label || "").trim();
  };

  const resolveMobileLabel = (lineId) => {
    const option = mobileById.get(String(mobileIdByLine[lineId] || ""));
    return String(option?.mobile || option?.label || "").trim();
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (selectedIds.length === 0) {
      setError(t("validation.selectAtLeastOneProduct"));
      return;
    }

    const formData = new FormData(e.currentTarget);
    const dates = { ...datesById };
    const invoices = { ...invoiceById };
    const notes = { ...notesById };
    selectedIds.forEach((id) => {
      if (formData.has(`delivery-date-${id}`)) {
        dates[id] = String(formData.get(`delivery-date-${id}`) ?? "");
      }
      if (formData.has(`invoice-${id}`)) {
        invoices[id] = String(formData.get(`invoice-${id}`) ?? "");
      }
      if (formData.has(`notes-${id}`)) {
        notes[id] = String(formData.get(`notes-${id}`) ?? "");
      }
    });

    const missingDate = selectedIds.find((id) => !dates[id]);
    if (missingDate) {
      setError(
        t("validation.fieldRequired", {
          field: t("dateTime.deliveryDate"),
        })
      );
      return;
    }

    setDatesById(dates);
    setInvoiceById(invoices);
    setNotesById(notes);

    onConfirm(
      selectedIds.map((lineId) => ({
        lineId,
        deliveryDate: dates[lineId],
        invoiceNumber: String(invoices[lineId] || "").trim(),
        deliveryNotes: String(notes[lineId] || "").trim(),
        challanToAddress: resolveAddressLabel(lineId),
        challanToMobile: resolveMobileLabel(lineId),
      }))
    );
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogPortal>
        <DialogOverlay className="bg-[#0a1230]/40 backdrop-blur-xs fixed inset-0 z-40 transition-opacity animate-in fade-in duration-200" />

        <DialogPrimitive.Content
          className={cn(
            "fixed left-[50%] top-[50%] z-50 translate-x-[-50%] translate-y-[-50%]",
            "w-[95vw] sm:w-[90vw] md:w-[720px] max-h-[85vh]",
            "bg-white rounded-2xl shadow-[0_30px_80px_-20px_rgba(30,51,131,0.45)] border border-white/60",
            "p-6 flex flex-col gap-4 font-sans focus:outline-hidden overflow-hidden",
            "duration-200 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95"
          )}
        >
          <div className="flex items-center justify-between shrink-0">
            <div className="flex items-center gap-2">
              <PackageCheck className="h-4.5 w-4.5 text-primary" />
              <DialogTitle className="text-[16px] font-bold text-foreground">
                {t("formActions.markAsDelivered")}
              </DialogTitle>
            </div>
            <DialogPrimitive.Close
              disabled={submitting}
              className="h-8 w-8 grid place-items-center rounded-lg per-file-hint hover:bg-workspace/60 hover:text-primary transition focus:outline-hidden disabled:opacity-50"
            >
              <X className="h-4 w-4" />
              <span className="sr-only">{t("formActions.close")}</span>
            </DialogPrimitive.Close>
          </div>

          <p className="text-xs per-file-hint font-semibold leading-relaxed shrink-0">
            {t("inventory.markDeliveredSelectHint")}
          </p>

          <form
            onSubmit={handleSubmit}
            className="flex flex-col gap-4 min-h-0 flex-1 overflow-hidden"
          >
            {products.length === 0 ? (
              <div className="rounded-xl border border-(--color-text-border)/60 bg-workspace/30 px-4 py-6 text-center text-xs font-semibold per-file-hint">
                {t("inventory.noDeliverableProducts")}
              </div>
            ) : (
              <div className="rounded-xl border border-(--color-text-border)/60 overflow-hidden min-h-0 flex flex-col">
                <div className="flex items-center gap-3 px-3.5 py-2.5 bg-gray-50/80 border-b border-(--color-text-border)/50 shrink-0">
                  <input
                    type="checkbox"
                    checked={allSelected}
                    ref={(el) => {
                      if (el) el.indeterminate = someSelected;
                    }}
                    onChange={toggleAll}
                    disabled={submitting}
                    className="h-4 w-4 rounded border-gray-300 text-primary focus:ring-accent cursor-pointer"
                    aria-label={t("formActions.selectAll")}
                  />
                  <span className="text-[11px] font-extrabold uppercase tracking-wider text-gray-500">
                    {t("formActions.selectAll")}
                  </span>
                </div>

                <div className="overflow-y-auto max-h-[48vh] divide-y divide-(--color-text-border)/40">
                  {products.map((product) => {
                    const id = String(product.id);
                    const checked = selectedIds.includes(id);
                    return (
                      <div key={id} className="px-3.5 py-3 space-y-3">
                        <label className="flex items-start gap-3 min-w-0 cursor-pointer">
                          <input
                            type="checkbox"
                            checked={checked}
                            onChange={() => toggleOne(id)}
                            disabled={submitting}
                            className="mt-0.5 h-4 w-4 rounded border-gray-300 text-primary focus:ring-accent cursor-pointer shrink-0"
                          />
                          <span className="min-w-0">
                            <span className="block text-xs font-bold text-foreground truncate">
                              {product.name || "—"}
                            </span>
                            <span className="block text-[11px] per-file-hint font-semibold mt-0.5">
                              {t("inventory.allocatedQty")}:{" "}
                              <span className="font-mono text-foreground">
                                {product.allocatedQty ?? "—"}
                              </span>
                              {product.batchNumber &&
                              product.batchNumber !== "—" ? (
                                <>
                                  {" · "}
                                  {t("inventory.batchNumber")}:{" "}
                                  <span className="font-mono text-foreground">
                                    {product.batchNumber}
                                  </span>
                                </>
                              ) : null}
                            </span>
                          </span>
                        </label>

                        <div className="pl-7 grid grid-cols-1 sm:grid-cols-2 gap-3">
                          <div>
                            <label className={fieldLabelClass}>
                              {t("dateTime.deliveryDate")}
                              {checked ? (
                                <span className="text-rose-500"> *</span>
                              ) : null}
                            </label>
                            <input
                              type="date"
                              name={`delivery-date-${id}`}
                              value={datesById[id] || ""}
                              onChange={(e) => setDate(id, e.target.value)}
                              disabled={submitting || !checked}
                              className={fieldInputClass}
                            />
                          </div>
                          <div>
                            <label className={fieldLabelClass}>
                              {t("inventory.invoiceNumber")}
                            </label>
                            <input
                              type="text"
                              name={`invoice-${id}`}
                              value={invoiceById[id] || ""}
                              onChange={(e) => setInvoice(id, e.target.value)}
                              disabled={submitting || !checked}
                              maxLength={30}
                              placeholder={t("inventory.invoiceNumber")}
                              className={fieldInputClass}
                            />
                          </div>
                          <div>
                            <label className={fieldLabelClass}>
                              {t("inventory.address")}
                              {loadingLocations ? (
                                <span className="normal-case tracking-normal font-semibold text-gray-400">
                                  {" "}
                                  · {t("common.loading")}
                                </span>
                              ) : null}
                            </label>
                            <Select
                              value={addressIdByLine[id] || ""}
                              onValueChange={(val) => setAddressId(id, val)}
                              disabled={
                                submitting ||
                                !checked ||
                                (loadingLocations && locations.length === 0)
                              }
                            >
                              <SelectTrigger className={selectTriggerClass}>
                                <SelectValue
                                  placeholder={t("inventory.selectAddress")}
                                />
                              </SelectTrigger>
                              <SelectContent
                                className="z-[100]"
                                viewportClassName="h-auto max-h-60 overflow-y-auto"
                              >
                                {locations.map((row) => (
                                  <SelectItem
                                    key={`deliver-address-${id}-${row.id}`}
                                    value={String(row.id)}
                                  >
                                    {row.label}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </div>
                          <div>
                            <label className={fieldLabelClass}>
                              {t("inventory.mobileNumber")}
                              {loadingMobiles ? (
                                <span className="normal-case tracking-normal font-semibold text-gray-400">
                                  {" "}
                                  · {t("common.loading")}
                                </span>
                              ) : null}
                            </label>
                            <Select
                              value={mobileIdByLine[id] || ""}
                              onValueChange={(val) => setMobileId(id, val)}
                              disabled={
                                submitting ||
                                !checked ||
                                (loadingMobiles && mobiles.length === 0)
                              }
                            >
                              <SelectTrigger className={selectTriggerClass}>
                                <SelectValue
                                  placeholder={t(
                                    "inventory.selectMobileNumber"
                                  )}
                                />
                              </SelectTrigger>
                              <SelectContent
                                className="z-[100]"
                                viewportClassName="h-auto max-h-60 overflow-y-auto"
                              >
                                {mobiles.map((row) => (
                                  <SelectItem
                                    key={`deliver-mobile-${id}-${row.id}`}
                                    value={String(row.id)}
                                  >
                                    {row.label}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </div>
                          <div className="sm:col-span-2">
                            <label className={fieldLabelClass}>
                              {t("inventory.deliveryNotes")}
                            </label>
                            <textarea
                              name={`notes-${id}`}
                              value={notesById[id] || ""}
                              onChange={(e) => setNotes(id, e.target.value)}
                              disabled={submitting || !checked}
                              rows={2}
                              placeholder={t(
                                "inventory.deliveryNotesPlaceholder"
                              )}
                              className={cn(
                                fieldInputClass,
                                "h-auto min-h-[64px] py-2 resize-y"
                              )}
                            />
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {error ? (
              <p className="text-[11px] text-rose-500 font-bold shrink-0">
                ⚠️ {error}
              </p>
            ) : null}

            <div className="flex items-center justify-end gap-3 pt-3 border-t border-(--color-text-border)/50 shrink-0">
              <button
                type="button"
                disabled={submitting}
                onClick={() => onOpenChange(false)}
                className="h-10 px-4 rounded-xl border border-(--color-text-border) per-file-hint font-bold text-xs hover:bg-gray-50 transition cursor-pointer disabled:opacity-50"
              >
                {t("formActions.cancel")}
              </button>
              <button
                type="submit"
                disabled={submitting || products.length === 0}
                className="h-10 px-5 rounded-xl bg-primary hover:bg-accent text-white font-bold text-xs transition shadow-[0_8px_20px_-8px_rgba(30,51,131,0.5)] cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {submitting
                  ? t("inventory.saving")
                  : t("formActions.save")}
              </button>
            </div>
          </form>
        </DialogPrimitive.Content>
      </DialogPortal>
    </Dialog>
  );
}
